import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { nanoid } from 'nanoid';
import { Table, TableBody, TableRow, TableCell, makeStyles } from '@material-ui/core';
import Zoom from '@material-ui/core/Zoom';
import LoadingSpinner from 'components/LoadingSpinner';
import { Message } from 'components/shared';
import { slugify } from 'utils/string-helpers';
import useNavigate from 'hooks/useNavigate';
import { useContextMenu } from 'react-contexify';
import { TableHeader } from '../common/components';
import getTableBCellFormat from './utils/getTableBCellFormat';
import useSorting from '../common/hooks';
import TypographyWithTooltip from '../../../shared/TypographyWithTooltip';

const TABLE_CONTEXT_MENU_ID = 'TABLE_CONTEXT_MENU_ID';

const useStyles = makeStyles(theme => ({
  row: {
    textDecoration: 'none',
    '& td': {
      fontSize: theme.customStyles.tableRow.fontSize,
      padding: theme.customStyles.tableRow.padding,
      '&:not(last-child)': {
        borderLeft: `1px solid ${theme.palette.grey.medium}`
      }
    }
  },
  linkRow: {
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
      boxShadow: theme.palette.boxShadow.default,
      backgroundColor: `${theme.palette.primary.main}40`
    },
    '& td': {
      fontSize: theme.customStyles.tableRow.fontSize,
      padding: theme.customStyles.tableRow.padding,
      '&:first-child': {
        maxWidth: theme.customStyles.nthChild.maxWidth,
        overflow: theme.customStyles.cuttingTextCompactMode.overflow,
        whiteSpace: theme.customStyles.cuttingTextCompactMode.whiteSpace,
        textOverflow: theme.customStyles.cuttingTextCompactMode.textOverflow
      },
      '&:nth-child(2)': {
        maxWidth: theme.customStyles.nthChild.maxWidth,
        overflow: theme.customStyles.cuttingTextCompactMode.overflow,
        whiteSpace: theme.customStyles.cuttingTextCompactMode.whiteSpace,
        textOverflow: theme.customStyles.cuttingTextCompactMode.textOverflow
      },
      '&:nth-child(3)': {
        overflow: theme.customStyles.cuttingTextCompactMode.overflow,
        whiteSpace: theme.customStyles.cuttingTextCompactMode.whiteSpace,
        textOverflow: theme.customStyles.cuttingTextCompactMode.textOverflow
      },
      '&:not(last-child)': {
        borderLeft: `1px solid ${theme.palette.grey.medium}`
      }
    }
  },
  footer: {
    backgroundColor: theme.palette.grey.medium,
    '& td': {
      color: theme.palette.primary.main,
      fontWeight: theme.typography.fontWeightBold,
      fontSize: theme.customStyles.tableTemf.fontSize,
      padding: theme.customStyles.tableRow.padding
    }
  },
  negative: {
    color: theme.palette.error.main
  },
  grey: {
    backgroundColor: '#a39e9e',
    color: theme.palette.white
  },
  lightGreen: {
    backgroundColor: '#06d151',
    color: theme.palette.white
  },
  lightRed: {
    backgroundColor: '#d44a4a',
    color: theme.palette.white
  },
  darkGreen: {
    backgroundColor: '#217540',
    color: theme.palette.white
  },
  darkRed: {
    backgroundColor: '#8c0303',
    color: theme.palette.white
  },

  title: {
    fontSize: theme.customStyles.tableRow.fontSize
  },
  numericValues: {
    textAlign: 'right'
  },
  transparent: {
    backgroundColor: 'transparent'
  }
}));

const TableB = ({ state, setTableExportData, widget, forwardRef }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { show } = useContextMenu();
  const { sortBy, sortOrder, getColumnSorting, onColumnSort } = useSorting();
  const { data: response, isLoading, isError, error } = state;

  const { overlay } = widget;
  const { widgetTitle } = overlay;

  const getMappedColumns = useCallback(() => {
    if (!response) return [];
    return response.header.map((item, index) => ({
      index,
      key: item ? slugify(item) : 'index',
      title: item,
      isSortable: false
    }));
  }, [response]);

  const getMappedRows = useCallback(() => {
    if (!response) return [];
    const columns = getMappedColumns();
    return response.data.map(row =>
      row.reduce(
        (result, next, index) => {
          const key = columns[index].key || 'index';
          return { ...result, [key]: next };
        },
        { uniqueKey: nanoid(6) }
      )
    );
  }, [response, getMappedColumns]);

  useEffect(() => {
    if (response) {
      const columns = getMappedColumns();
      const rows = [...getMappedRows()];
      setTableExportData({
        title: widgetTitle && slugify(widgetTitle),
        columns,
        rows
      });
    }
  }, [response, getMappedColumns, getMappedRows, setTableExportData, widgetTitle]);

  if (isError) {
    return <Message type="error" text={error} />;
  }

  if (isLoading || !response) {
    return <LoadingSpinner />;
  }

  function handleContextMenu(e, link) {
    const baseUrl = window.location.origin;
    show(e, {
      id: TABLE_CONTEXT_MENU_ID,
      props: {
        tabHref: `${baseUrl}${link}`
      }
    });
  }

  const columns = getMappedColumns();
  const rows = getMappedRows();

  let calculatedRow = -1;
  const calculateRow = currentCol => {
    if (currentCol === 0) {
      // eslint-disable-next-line no-plusplus
      calculatedRow++;
    }
    const currentRow = calculatedRow;
    return { currentRow, currentCol };
  };
  const renderRow = row => {
    return columns.map((column, index) => {
      if (column.key === 'timeserieslinkid' || column.key === 'timeserieslinktype') {
        return null;
      }
      const { currentRow, currentCol } = calculateRow(index);

      const { value, classList } = getTableBCellFormat(row[column.key], currentRow, currentCol);

      const key = `${column.key}-${row.uniqueKey}`;
      return (
        <TableCell key={key} className={classList.map(c => classes[c]).join(' ')}>
          <TypographyWithTooltip
            variant="subtitle1"
            className={classes.title}
            gutterBottom
            tooltipProps={{
              value,
              placement: 'right',
              TransitionComponent: Zoom,
              arrow: true
            }}
          >
            <span className={classList.map(c => classes[c]).join(' ')}>
              {currentCol === 0 ? `${currentRow + 1}. ${value}` : value}
            </span>
          </TypographyWithTooltip>
        </TableCell>
      );
    });
  };

  const renderBody = () => {
    const sortKey = sortBy || 'index';
    const sortedRows = rows.sort(getColumnSorting(sortKey, sortOrder));

    const rowElements = sortedRows.map(row => {
      if (row.timeserieslinkid) {
        return (
          <TableRow
            onClick={() => navigate(`/canned/${row.timeserieslinkid}`)}
            onContextMenu={e => handleContextMenu(e, `/canned/${row.timeserieslinkid}`)}
            key={row.uniqueKey}
            className={classes.linkRow}
          >
            {renderRow(row)}
          </TableRow>
        );
      }
      return (
        <TableRow key={row.uniqueKey} className={classes.row}>
          {renderRow(row)}
        </TableRow>
      );
    });

    return <>{rowElements}</>;
  };

  return (
    <Table ref={forwardRef}>
      <TableHeader columns={columns} onColumnSort={onColumnSort} />
      <TableBody>{renderBody()}</TableBody>
    </Table>
  );
};

TableB.propTypes = {
  state: PropTypes.shape({
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
    error: PropTypes.string,
    data: PropTypes.shape({
      rows: PropTypes.arrayOf(PropTypes.shape({}))
    })
  }),
  widget: PropTypes.shape({
    title: PropTypes.string,
    colSpan: PropTypes.number.isRequired,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    dateRangeType: PropTypes.string,
    dateMatchType: PropTypes.number,
    isTitleDisplayed: PropTypes.bool.isRequired,
    isDateDisplayed: PropTypes.bool.isRequired,
    isLegendDisplayed: PropTypes.bool.isRequired,
    isChartTooltipDisplayed: PropTypes.bool.isRequired,
    id: PropTypes.string.isRequired,
    dateRangeTypeId: PropTypes.number.isRequired,
    masterObjectId: PropTypes.number.isRequired,
    masterObjectType: PropTypes.string,
    masterObjectName: PropTypes.string.isRequired,
    benchmarks: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        id: PropTypes.number.isRequired
      })
    ),
    masterObjectTypeId: PropTypes.number,
    overlay: PropTypes.shape({
      widgetTitle: PropTypes.string,
      baseWidget: PropTypes.shape({
        name: PropTypes.string.isRequired,
        widgetType: PropTypes.shape({
          name: PropTypes.string.isRequired
        }).isRequired
      }).isRequired
    }).isRequired
  }).isRequired,
  setTableExportData: PropTypes.func,
  forwardRef: PropTypes.shape({})
};

TableB.defaultProps = {
  state: {},
  setTableExportData: null,
  forwardRef: null
};

export default TableB;
